import {
    ComponentDescription,
    ComponentKind,
    InputOutputTables,
    MutatingScreenKind,
    TableGlideType,
    PropertyDescription,
    getPrimitiveColumns,
} from "../description";
import { ComponentHandlerBase } from "./handler";
import { ComponentBuilder, ComponentConfiguratorContext, ComponentDescriptor } from ".";
import { SerializedComponent } from "../../components/SerializedApp";
import { getDocURL } from "../../docUrl";
import { makeValidationFlagForProperty, setColumnInRowForEditComponent } from "../builder-utils";
import { ColumnPropertyHandler, ColumnPropertyFlag, isRequiredPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import { getFeatureSetting } from "../../feature-settings";
import { SerializedElementBuilder } from "../serialized-component";
import { getColumnProperty } from "./property-utils";

const ComponentKindLocationEntry: ComponentKind = "location-entry";

const propertyNamePropertyHandler = new ColumnPropertyHandler(
    "propertyName",
    "Column",
    [
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.EditedInApp,
        // ColumnPropertyFlag.DefaultCaption,
    ],
    undefined,
    ["location", "coordinates"],
    getPrimitiveColumns,
    "string",
    PropertySection.Data
);

interface LocationEntryComponentDescription extends ComponentDescription {
    readonly propertyName: PropertyDescription;
    readonly isRequired: PropertyDescription | undefined;
}

export class LocationEntryComponentHandler extends ComponentHandlerBase<LocationEntryComponentDescription> {
    constructor() {
        super(ComponentKindLocationEntry);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public needValidation(): boolean {
        return true;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: LocationEntryComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = propertyNamePropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return b.emptyUI;

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            b.getProperty(propertyName, outputContext, tables.output),
            undefined,
            tables.output,
            undefined,
            b.getContext
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const isRequired = isRequiredPropertyHandler.getSwitch(desc);

        return b.let(
            lets,
            b.ui("LocationEntry", {
                value: valueToModify,
                onChange: action,
                isEnabled,
                validFlag: !isRequired && mutatingScreenKind !== undefined ? validFlag : undefined,
                isRequired: b.constant(isRequired),
            })
        );
    }

    public getDescriptor(
        _desc: LocationEntryComponentDescription,
        _table: TableGlideType | undefined,
        ccc: ComponentConfiguratorContext,
        _mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<LocationEntryComponentDescription> {
        return {
            name: "Location",
            description: "Enter the current location",
            img: "componentLocation",
            group: "Entry Fields",
            isLegacy: !getFeatureSetting("locationEntry"),
            helpUrl: getDocURL("locationEntry"),
            quotaWarning: ccc.eminenceFlags.proComponents ? undefined : "Enter 10 locations per month on free apps.",
            properties: [...this.getBasePropertyDescriptors(), propertyNamePropertyHandler, isRequiredPropertyHandler],
        };
    }

    public makeValidationFlag(
        b: SerializedElementBuilder,
        desc: LocationEntryComponentDescription,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined {
        const isRequired = isRequiredPropertyHandler.getSwitch(desc);
        if (!isRequired) return undefined;
        return makeValidationFlagForProperty(b, outputContext, getColumnProperty(desc.propertyName), table);
    }
}
